/* eslint-disable react/jsx-no-literals -- pre-i18n migration; replace literals with t() and remove this directive (see docs/i18n-migration-plan.md) */
/**
 * PromoBannerDisplay — Reusable component that fetches and renders active promo banners.
 * Place on any page with: <PromoBannerDisplay placement="top_bar" />
 * Handles auto-fetch, impression tracking, click tracking, and dismissal.
 */
import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import promoBannerService from '@services/promoBannerService';

// Track dismissed banners in memory for the session
const dismissedBanners = new Set();

const PromoBannerDisplay = ({ placement = 'top_bar', maxBanners = 1 }) => {
  const [banners, setBanners] = useState([]);
  const trackedImpressions = useRef(new Set());
  const navigate = useNavigate();

  useEffect(() => {
    promoBannerService.getActive(placement)
      .then(r => {
        const active = (r.data || []).filter(b => !dismissedBanners.has(b.id));
        setBanners(active.slice(0, maxBanners));
      })
      .catch(() => {});
  }, [placement, maxBanners]);

  // Track impressions once per banner per session
  useEffect(() => {
    banners.forEach(b => {
      if (!trackedImpressions.current.has(b.id)) {
        trackedImpressions.current.add(b.id);
        promoBannerService.track(b.id, 'impression', window.location.pathname).catch(() => {});
      }
    });
  }, [banners]);

  const handleClick = (banner) => {
    promoBannerService.track(banner.id, 'click', window.location.pathname).catch(() => {});
    if (banner.ctaUrl) {
      if (banner.ctaUrl.startsWith('http')) {
        window.open(banner.ctaUrl, '_blank');
      } else {
        navigate(banner.ctaUrl);
      }
    }
  };

  const handleDismiss = (banner) => {
    promoBannerService.track(banner.id, 'dismiss', window.location.pathname).catch(() => {});
    dismissedBanners.add(banner.id);
    setBanners(prev => prev.filter(b => b.id !== banner.id));
  };

  if (!banners.length) return null;

  return (
    <>
      {banners.map(b => {
        const bg = b.bgGradientEnd
          ? `linear-gradient(135deg, ${b.bgColor}, ${b.bgGradientEnd})`
          : b.bgColor || '#9B30FF';
        const color = b.textColor || '#FFFFFF';

        // ── Slim top bar ──
        if (b.size === 'slim') {
          return (
            <div
              key={b.id}
              style={{ background: bg, color, padding: '8px 16px', textAlign: 'center', position: 'relative', fontSize: '0.85rem' }}
            >
              {b.badgeText && (
                <span style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 4, padding: '2px 8px', marginRight: 8, fontSize: '0.75rem', fontWeight: 600 }}>
                  {b.badgeText}
                </span>
              )}
              <span>{b.headline || b.body}</span>
              {b.ctaText && (
                <button
                  onClick={() => handleClick(b)}
                  style={{ background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)', color, borderRadius: 4, padding: '2px 12px', marginLeft: 12, fontSize: '0.8rem', cursor: 'pointer', fontWeight: 500 }}
                >
                  {b.ctaText}
                </button>
              )}
              {b.dismissible && (
                <button
                  onClick={() => handleDismiss(b)}
                  style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color, fontSize: '1rem', cursor: 'pointer', opacity: 0.7 }}
                  aria-label="Dismiss"
                >
                  ✕
                </button>
              )}
            </div>
          );
        }

        // ── Large hero-width ──
        if (b.size === 'large') {
          return (
            <div
              key={b.id}
              style={{ background: bg, color, padding: '28px 24px', borderRadius: 12, margin: '12px 0', position: 'relative', overflow: 'hidden' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
                {b.imageUrl && (
                  <img src={b.imageUrl} alt="" style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
                )}
                <div style={{ flex: 1, minWidth: 200 }}>
                  {b.badgeText && (
                    <span style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 4, padding: '2px 10px', fontSize: '0.7rem', fontWeight: 600, marginBottom: 6, display: 'inline-block' }}>
                      {b.badgeText}
                    </span>
                  )}
                  {b.headline && <h5 style={{ margin: '4px 0', fontWeight: 700, fontSize: '1.15rem' }}>{b.headline}</h5>}
                  {b.body && <p style={{ margin: 0, opacity: 0.9, fontSize: '0.9rem' }}>{b.body}</p>}
                </div>
                {b.ctaText && (
                  <Button
                    variant="light"
                    size="sm"
                    style={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                    onClick={() => handleClick(b)}
                  >
                    {b.ctaText} →
                  </Button>
                )}
              </div>
              {b.dismissible && (
                <button
                  onClick={() => handleDismiss(b)}
                  style={{ position: 'absolute', right: 12, top: 12, background: 'rgba(255,255,255,0.15)', border: 'none', color, fontSize: '0.85rem', cursor: 'pointer', borderRadius: 4, width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  aria-label="Dismiss"
                >
                  ✕
                </button>
              )}
            </div>
          );
        }

        // ── Standard card (default) ──
        return (
          <div
            key={b.id}
            style={{ background: bg, color, padding: '16px 20px', borderRadius: 10, margin: '12px 0', position: 'relative', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}
          >
            {b.imageUrl && (
              <img src={b.imageUrl} alt="" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
            )}
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                {b.badgeText && (
                  <span style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 4, padding: '1px 8px', fontSize: '0.65rem', fontWeight: 600 }}>
                    {b.badgeText}
                  </span>
                )}
                {b.headline && <strong style={{ fontSize: '0.95rem' }}>{b.headline}</strong>}
              </div>
              {b.body && <p style={{ margin: 0, opacity: 0.85, fontSize: '0.82rem', lineHeight: 1.4 }}>{b.body}</p>}
            </div>
            {b.ctaText && (
              <Button
                variant="light"
                size="sm"
                style={{ fontWeight: 600, whiteSpace: 'nowrap', fontSize: '0.8rem' }}
                onClick={() => handleClick(b)}
              >
                {b.ctaText}
              </Button>
            )}
            {b.dismissible && (
              <button
                onClick={() => handleDismiss(b)}
                style={{ position: 'absolute', right: 8, top: 8, background: 'none', border: 'none', color, fontSize: '0.85rem', cursor: 'pointer', opacity: 0.6 }}
                aria-label="Dismiss"
              >
                ✕
              </button>
            )}
          </div>
        );
      })}
    </>
  );
};

export default PromoBannerDisplay;
